import {
  HubConnectionBuilder,
  LogLevel,
  RetryContext,
} from '@microsoft/signalr'
import { APIS, ENV, LOADERS } from '../config/constants'
import { useLoader } from 'src/hooks/useLoader'
import { useSignalRStore } from 'src/stores'
import { useClientContextStore } from 'src/client/stores'
import { useAuthStore } from 'src/common/store'
import { signalRBus } from '../signalR/signalRBus'
import { registerSignalRHandlers } from '../signalR'
import { SIGNAL_R_SCOPES, SystemResponse } from 'src/signalR/types'
import { storeToRefs } from 'pinia'
import { jwtDecode } from 'jwt-decode'

interface SignalRConnectionInfo {
  url: string
  accessToken: string
}

interface SignalRTokenClaims {
  exp?: number
}

const SIGNALR_TOKEN_REFRESH_BUFFER_MS = 60_000
const SIGNALR_FALLBACK_REFRESH_MS = 5 * 60_000

let connectionsInitialization: Promise<void> | null = null
let orgConnectionInitialization: Promise<void> | null = null
let userConnectionInitialization: Promise<void> | null = null

function getTokenRefreshAt(accessToken: string) {
  try {
    const { exp } = jwtDecode<SignalRTokenClaims>(accessToken)
    if (exp) {
      return exp * 1000 - SIGNALR_TOKEN_REFRESH_BUFFER_MS
    }
  } catch {
    // Azure SignalR tokens are JWTs, but use a bounded fallback if that changes.
  }

  return Date.now() + SIGNALR_FALLBACK_REFRESH_MS
}

function createAccessTokenFactory(
  initialAccessToken: string,
  refreshConnectionInfo: () => Promise<
    SignalRConnectionInfo | null | undefined
  >,
) {
  let accessToken = initialAccessToken
  let refreshAt = getTokenRefreshAt(accessToken)
  let refreshPromise: Promise<string> | null = null

  return async () => {
    if (Date.now() < refreshAt) {
      return accessToken
    }

    if (!refreshPromise) {
      refreshPromise = refreshConnectionInfo()
        .then((connectionInfo) => {
          if (!connectionInfo?.accessToken) {
            throw new Error('SignalR token refresh failed')
          }

          accessToken = connectionInfo.accessToken
          refreshAt = getTokenRefreshAt(accessToken)
          return accessToken
        })
        .finally(() => {
          refreshPromise = null
        })
    }

    return refreshPromise
  }
}

export const useSignalR = () => {
  const signalRStore = useSignalRStore()
  const {
    orgConnection,
    userConnection,
    isUserConnected,
    isOrgConnected,
    isOrgDisconnecting,
    isUserDisconnecting,
  } = storeToRefs(signalRStore)
  const { updateOrgConnection, updateUserConnection } = signalRStore

  const authStore = useAuthStore()
  const clientContextStore = useClientContextStore()
  const { useDeleteBulk: leaveAllUserGroups } = useLoader(LOADERS.signalR, {
    uri: APIS.groups,
  })

  function initializeConnections() {
    if (connectionsInitialization) return connectionsInitialization

    if (!signalRStore.handlersRegistered) {
      signalRStore.handlersRegistered = true
      registerSignalRHandlers()
    }

    connectionsInitialization = Promise.all([
      initializeOrgConnection(),
      initializeUserConnection(),
    ])
      .then(() => undefined)
      .finally(() => {
        connectionsInitialization = null
      })

    return connectionsInitialization
  }

  function initializeOrgConnection() {
    if (orgConnectionInitialization) return orgConnectionInitialization

    orgConnectionInitialization = startOrgConnection().finally(() => {
      orgConnectionInitialization = null
    })
    return orgConnectionInitialization
  }

  async function startOrgConnection() {
    try {
      await stopOrgConnection()

      const { usePostLoader: getOrgConnectionInfo } =
        useLoader<SignalRConnectionInfo>(LOADERS.signalR, {
          uri: APIS.negotiate,
        })
      const orgConnectionData = await getOrgConnectionInfo({})
      if (!orgConnectionData?.url || !orgConnectionData.accessToken) {
        throw new Error('SignalR organization negotiation failed')
      }
      const { url: orgUrl } = orgConnectionData
      const orgOptions = {
        accessTokenFactory: createAccessTokenFactory(
          orgConnectionData.accessToken,
          () => getOrgConnectionInfo({}),
        ),
      }

      const connection = new HubConnectionBuilder()
        .withUrl(orgUrl, orgOptions)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: calculateRetry,
        })
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      connection?.on(SIGNAL_R_SCOPES.system, (res: SystemResponse) => {
        signalRBus.emit(SIGNAL_R_SCOPES.system, res)
      })
      connection?.onreconnected(() => {
        signalRBus.emit(SIGNAL_R_SCOPES.system, null, { reconnect: true })
      })
      await connection.start()
      signalRBus.emit(SIGNAL_R_SCOPES.system, null, { reconnect: true })
      updateOrgConnection(connection)
    } catch {
      await stopOrgConnection()
    }
  }

  function initializeUserConnection() {
    if (userConnectionInitialization) return userConnectionInitialization

    userConnectionInitialization = startUserConnection().finally(() => {
      userConnectionInitialization = null
    })
    return userConnectionInitialization
  }

  async function startUserConnection() {
    try {
      await stopUserConnection()

      const email = authStore.user?.email
      const orgId = authStore.currentOrgId
      const centerId = authStore.currentCenterId
      const familyId = clientContextStore.family?.id
      const role = authStore.role
      const availableCenters = authStore.availableCenters || []

      const roleGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `role:${orgId}:${center}:${role}`)
          : authStore.isOrgAdmin
            ? [`role:${orgId}::${role}`]
            : [`role:${orgId}:${centerId}:${role}`]

      const centerGroups =
        availableCenters.length > 0
          ? availableCenters.map((center) => `center:${center}`)
          : centerId
            ? [`center:${centerId}`]
            : []

      const userGroups = [
        `user:${email}`,
        ...roleGroups,
        ...centerGroups,
        ...(familyId ? [`family:${familyId}`] : []),
      ]

      const { usePostLoader: getUserConnectionInfo } =
        useLoader<SignalRConnectionInfo>(LOADERS.signalR, {
          uri: `${APIS.negotiate}/${APIS.user}`,
        })
      const userConnectionData = await getUserConnectionInfo({
        groupNames: userGroups,
      })
      if (!userConnectionData?.url || !userConnectionData.accessToken) {
        throw new Error('SignalR user negotiation failed')
      }
      const { url: userUrl } = userConnectionData
      const userOptions = {
        accessTokenFactory: createAccessTokenFactory(
          userConnectionData.accessToken,
          () => getUserConnectionInfo({}),
        ),
      }

      const connection = new HubConnectionBuilder()
        .withUrl(userUrl, userOptions)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: calculateRetry,
        })
        .configureLogging(
          process.env.ENV === ENV.DEV ? LogLevel.Information : LogLevel.None,
        )
        .build()

      connection?.on(
        SIGNAL_R_SCOPES.notification,
        async (res: SystemResponse) => {
          signalRBus.emit(SIGNAL_R_SCOPES.notification, res)
        },
      )

      connection?.on(SIGNAL_R_SCOPES.message, (res: SystemResponse) => {
        signalRBus.emit(SIGNAL_R_SCOPES.message, res)
      })
      connection?.onreconnected(() => {
        signalRBus.emit(SIGNAL_R_SCOPES.message, null, { reconnect: true })
      })
      await connection?.start()
      signalRBus.emit(SIGNAL_R_SCOPES.message, null, { reconnect: true })
      updateUserConnection(connection)
    } catch {
      await stopUserConnection()
    }
  }

  function reconnectIfNeeded() {
    if (isOrgDisconnecting.value || isUserDisconnecting.value) {
      setTimeout(reconnectIfNeeded, 3000)
      return
    }
    if (!isOrgConnected.value) {
      void initializeOrgConnection()
    }
    if (!isUserConnected.value) {
      void initializeUserConnection()
    }
  }

  async function stopConnections() {
    await Promise.all([stopOrgConnection(), stopUserConnection()])
  }

  async function stopOrgConnection() {
    if (!orgConnection.value) return
    await orgConnection.value.stop()
  }

  async function stopUserConnection() {
    if (!userConnection.value) return
    if (isUserConnected.value) {
      void leaveAllUserGroups({}).catch(() => undefined)
    }
    await userConnection.value.stop()
  }

  function calculateRetry(context: RetryContext) {
    if (context.previousRetryCount >= 20) {
      return null
    }
    return getDelay(context)
  }

  function getDelay(context: RetryContext) {
    if (context.previousRetryCount < 2) return 2000
    if (context.previousRetryCount < 5) return 5000
    if (context.previousRetryCount < 10) return 30000
    return 60000
  }

  return {
    initializeConnections,
    reconnectIfNeeded,
    stopConnections,
  }
}
